import React from 'react';

import EmailIcon from '@mui/icons-material/Email';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';

import { styles } from '../styles.ts';

// --- Types ---

type EmailVerificationMode = 'email_guess' | 'email_code';

interface EmailVerificationProps {
    mode: EmailVerificationMode;
    onSubmitEmailGuess?: (email: string | undefined) => Promise<boolean>;
    onSubmitEmailCode?: (code: string | undefined) => Promise<boolean>;
    emailGuessValue?: string;
    error?: string | null;
    isLoading?: boolean;
    hintEnabled?: boolean;
    hintValue?: string;
}

// --- Constants ---

const CODE_LENGTH = 6;

// --- Helpers ---

const isValidEmail = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
};

// --- Component ---

export const EmailVerification = ({
    mode,
    onSubmitEmailGuess,
    onSubmitEmailCode,
    emailGuessValue = '',
    error,
    isLoading,
    hintEnabled,
    hintValue,
}: EmailVerificationProps) => {
    const isEmailGuessMode = mode === 'email_guess';

    const [value, setValue] = React.useState<string>(() => (isEmailGuessMode ? emailGuessValue : ''));
    const [localError, setLocalError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setValue(isEmailGuessMode ? emailGuessValue : '');
    }, [isEmailGuessMode, emailGuessValue]);

    React.useEffect(() => {
        if (error) setLocalError(null);
    }, [error]);

    const handleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>): void => {
            setLocalError(null);

            if (isEmailGuessMode) {
                setValue(event.target.value);
            } else {
                setValue(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
            }
        },
        [isEmailGuessMode]
    );

    const handleSubmit = React.useCallback(async (): Promise<void> => {
        const trimmed = value.trim();
        if (!trimmed) return;

        setLocalError(null);

        try {
            if (isEmailGuessMode) {
                if (!isValidEmail(trimmed)) {
                    setLocalError('Please enter a valid email address');
                    return;
                }
                await onSubmitEmailGuess?.(trimmed);
            } else {
                if (trimmed.length < 4) {
                    setLocalError('Please enter the verification code');
                    return;
                }
                await onSubmitEmailCode?.(trimmed);
            }
        } catch (submissionError) {
            const message =
                submissionError instanceof Error ? submissionError.message : 'An error occurred. Please try again.';
            setLocalError(message);
        }
    }, [isEmailGuessMode, onSubmitEmailGuess, onSubmitEmailCode, value]);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>): void => {
            if (event.key === 'Enter') handleSubmit();
        },
        [handleSubmit]
    );

    const getTitle = (): string => (isEmailGuessMode ? 'Verify your email' : 'Enter verification code');

    const getSubtitle = (): string => {
        if (isEmailGuessMode) {
            return hintEnabled && hintValue
                ? `Enter your email address. Hint: ${hintValue}`
                : 'Enter your email address to receive a verification code.';
        }
        return hintValue
            ? `Enter the verification code sent to ${hintValue}`
            : 'Enter the verification code sent to your email.';
    };

    const isSubmitDisabled = (): boolean => {
        if (isLoading) return true;
        const trimmed = value.trim();
        if (!trimmed) return true;
        return isEmailGuessMode ? !isValidEmail(trimmed) : trimmed.length < 4;
    };

    const displayError = error ?? localError;

    return (
        <Box sx={styles.container}>
            <Box sx={styles.iconWrapper}>
                <EmailIcon sx={styles.icon} />
            </Box>

            <Box sx={styles.headerSection}>
                <Typography variant="h6" sx={styles.title}>
                    {getTitle()}
                </Typography>
                <Typography variant="body2" sx={styles.subtitle}>
                    {getSubtitle()}
                </Typography>
            </Box>

            {displayError && (
                <Alert severity="error" sx={styles.alert}>
                    {displayError}
                </Alert>
            )}

            <TextField
                fullWidth
                type={isEmailGuessMode ? 'email' : 'text'}
                placeholder={isEmailGuessMode ? 'your@email.com' : '000000'}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoComplete={isEmailGuessMode ? 'email' : 'one-time-code'}
                inputProps={{
                    maxLength: isEmailGuessMode ? 254 : CODE_LENGTH,
                    inputMode: isEmailGuessMode ? 'email' : 'numeric',
                    pattern: isEmailGuessMode ? undefined : '[0-9]*',
                    'aria-label': isEmailGuessMode ? 'Email input' : 'Verification code input',
                }}
                sx={isEmailGuessMode ? styles.emailInput : styles.codeInput}
            />

            <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitDisabled()}
                sx={styles.submitButton}
            >
                {isLoading ? 'Verifying...' : isEmailGuessMode ? 'Send code' : 'Verify code'}
            </Button>
        </Box>
    );
};
